import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { updateCADClassification } from '../../services/cad.service';
import type { CADModel } from '../../types/cad';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import CADClassificationFields from './CADClassificationFields';
import {
  EMPTY_CLASSIFICATION_DRAFT,
  classificationFromDraft,
  classificationToDraft,
  type CADClassificationDraft,
} from './classification';

interface CADClassificationDialogProps {
  model: CADModel | null;
  onClose: () => void;
  onSaved: (model: CADModel) => void;
}

const CADClassificationDialog: React.FC<CADClassificationDialogProps> = ({
  model,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<CADClassificationDraft>(EMPTY_CLASSIFICATION_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(model
      ? classificationToDraft(model.classification)
      : EMPTY_CLASSIFICATION_DRAFT);
    setError(null);
  }, [model]);

  const save = async () => {
    if (!model) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateCADClassification(model, classificationFromDraft(draft));
      onSaved(updated);
      onClose();
    } catch (saveError) {
      setError(resolveApiErrorMessage(t, saveError, {
        fallbackKey: 'cad.classification.updateFailed',
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(model)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cad.classification.editTitle')}</DialogTitle>
          <DialogDescription>
            {model?.filename} — {t('cad.classification.editDescription')}
          </DialogDescription>
        </DialogHeader>
        <CADClassificationFields
          value={draft}
          onChange={setDraft}
          idPrefix="cad-classification-edit"
          disabled={saving}
        />
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? t('cad.classification.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CADClassificationDialog;
